/**
 * Tenant theme application. The whole stylesheet is built on the HSL
 * `--primary` CSS variable (see `src/index.css`). When the request lands
 * on `bifx.localhost`, `/tenant/current` returns the tenant's hex
 * `primaryColor`; we convert it to HSL components and inject onto
 * `:root` so every `bg-primary`, gradient, button, and accent flips at
 * once without touching individual components.
 */

/** Parse a `#RRGGBB` string to {r,g,b} 0-255. */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

/**
 * RGB → HSL. Returns `{h: 0–360, s: 0–100, l: 0–100}` integers (rounded)
 * so the CSS variable lands in the same shape as the hand-authored
 * `--primary: 333 86% 56%`.
 */
function rgbToHsl(r: number, g: number, b: number) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: h = (gn - bn) / d + (gn < bn ? 6 : 0); break;
      case gn: h = (bn - rn) / d + 2; break;
      case bn: h = (rn - gn) / d + 4; break;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function hexToHslString(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  return `${h} ${s}% ${l}%`;
}

/** WCAG relative luminance of an sRGB colour. */
function relLum({ r, g, b }: { r: number; g: number; b: number }) {
  const ch = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

/**
 * Darken (keeping hue) until the colour clears 4.5:1 against white, so a
 * light brand colour stays legible as text/borders on light surfaces.
 */
function inkOnWhite(rgb: { r: number; g: number; b: number }) {
  let c = { ...rgb };
  for (let i = 0; i < 40 && (1.05 / (relLum(c) + 0.05)) < 4.5; i++) {
    c = { r: Math.round(c.r * 0.93), g: Math.round(c.g * 0.93), b: Math.round(c.b * 0.93) };
  }
  return c;
}

const toHsl = (c: { r: number; g: number; b: number }) => {
  const { h, s, l } = rgbToHsl(c.r, c.g, c.b);
  return { h, s, l, str: `${h} ${s}% ${l}%` };
};

/**
 * Publish the tenant's brand colour as --tenant-* variables. index.css maps
 * them onto --primary / --accent per theme: dark mode uses the raw colour,
 * light mode the deepened "ink" tone — raw gold on white is only ~2:1.
 */
export function applyTenantTheme(hexColor: string | null | undefined) {
  const root = document.documentElement;
  const vars = [
    "--tenant-primary", "--tenant-primary-fg", "--tenant-primary-ink",
    "--tenant-accent", "--tenant-accent-ink",
  ];
  // Older builds wrote these directly; clear them so the stylesheet decides.
  ["--primary", "--primary-fg", "--primary-soft", "--accent", "--accent-fg", "--ring"].forEach((v) => root.style.removeProperty(v));
  if (!hexColor) {
    vars.forEach((v) => root.style.removeProperty(v));
    return;
  }
  const rgb = hexToRgb(hexColor);
  if (!rgb) return;
  const raw = toHsl(rgb);
  const ink = toHsl(inkOnWhite(rgb));
  // Text on the raw fill: near-black on light brand colours, white on dark.
  const fg = relLum(rgb) > 0.36 ? "36 10% 4%" : "0 0% 100%";

  root.style.setProperty("--tenant-primary", raw.str);
  root.style.setProperty("--tenant-primary-fg", fg);
  root.style.setProperty("--tenant-primary-ink", ink.str);
  // Accent = a lifted tone of the same hue for two-stop gradients.
  root.style.setProperty("--tenant-accent", `${raw.h} ${Math.min(100, raw.s + 25)}% ${Math.min(78, raw.l + 14)}%`);
  root.style.setProperty("--tenant-accent-ink", `${ink.h} ${Math.min(100, ink.s + 8)}% ${Math.min(48, ink.l + 10)}%`);
}
